import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const read = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch (_) { return fallback; }
};
const save = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
const pageNames = [
  ['/applications', 'Applications'],
  ['/top-producer-view', 'My day'], ['/pipeline/crm', 'Customer pipeline'],
  ['/pipeline/inventory', 'Inventory pipeline'], ['/pipeline', 'Deal pipeline'],
  ['/crm', 'Customers'], ['/contract', 'Deals & contracts'], ['/inventory', 'Inventory'],
  ['/messages', 'Messages & campaigns'], ['/service-work-orders', 'Service work orders'],
  ['/service-requests', 'Service requests'], ['/reports', 'Reports'], ['/schedule', 'Schedule'],
  ['/sales-academy', 'Sales academy'], ['/training-resources', 'Training resources'],
  ['/conversation-bank', 'Conversation bank'], ['/dochub', 'Document hub'],
  ['/utilities', 'Tools'], ['/admin', 'Administration'], ['/profile', 'My profile'],
  ['/setup', 'Installation'], ['/walkthrough', 'Walkthroughs']
];
export const workspaceTitle = pathname => (pageNames.find(([path]) => pathname === path || pathname.startsWith(path + '/')) || ['', 'Workspace'])[1];
export const locationLabel = record => record && typeof record === 'object'
  ? [record.fullName || record.name || record.location, record.city].filter(Boolean).join(' · ')
  : '';

function WorkspaceHeader({ LegacyHeader, ...props }) {
  const location = useLocation();
  const user = read('user', {});
  const preferenceKey = 'mhc-workspace-layout:' + (user._id || 'session');
  const [classic, setClassic] = useState(() => read(preferenceKey, {}) .classic === true);
  const [compact, setCompact] = useState(() => read(preferenceKey, {}).compact === true);
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useRef(null);
  const menuButton = useRef(null);
  const modern = !props.noMenu && !classic;
  const assigned = read('user-sales-location', null);
  const store = locationLabel(assigned);

  useEffect(() => {
    document.body.classList.toggle('mhc-workspace', modern);
    document.body.classList.toggle('mhc-workspace-compact', modern && compact);
    return () => { document.body.classList.remove('mhc-workspace', 'mhc-workspace-compact'); };
  }, [modern, compact]);
  useEffect(() => { save(preferenceKey, { classic, compact }); }, [classic, compact, preferenceKey]);
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!modern) return;
    const keyboard = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && props.onWorkspaceSearch) {
        event.preventDefault(); props.onWorkspaceSearch();
      }
      if (event.key === 'Escape' && menuOpen) { setMenuOpen(false); menuButton.current && menuButton.current.focus(); }
    };
    window.addEventListener('keydown', keyboard);
    return () => window.removeEventListener('keydown', keyboard);
  }, [modern, menuOpen, props.onWorkspaceSearch]);
  useEffect(() => {
    if (!menuOpen || !nav.current) return;
    const first = nav.current.querySelector('a, button');
    if (first) first.focus();
  }, [menuOpen]);

  if (props.noMenu) return <LegacyHeader {...props} />;
  if (classic) return <><LegacyHeader {...props} /><button type="button" className="mhcw-restore" onClick={() => setClassic(false)}>Use new workspace</button></>;
  return <>
    <a href="#mhcw-main" className="mhcw-skip">Skip to workspace</a>
    <header className="mhcw-topbar">
      <button ref={menuButton} type="button" className="mhcw-menu-button" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={menuOpen} aria-controls="mhcw-navigation" onClick={() => setMenuOpen(!menuOpen)}>☰</button>
      <div className="mhcw-heading"><span>MHC WORKSPACE</span><strong>{workspaceTitle(location.pathname)}</strong></div>
      <div className="mhcw-topbar-actions">
        {props.onWorkspaceSearch && <button type="button" className="mhcw-search" onClick={props.onWorkspaceSearch}><span>Search CRM</span><kbd>⌘ / Ctrl K</kbd></button>}
        {store && <span className="mhcw-store" title={'Assigned location: ' + store}>{store}</span>}
        <button type="button" className="mhcw-density" aria-pressed={compact} onClick={() => setCompact(!compact)}>{compact ? 'Roomy view' : 'Compact view'}</button>
      </div>
    </header>
    {menuOpen && <button type="button" className="mhcw-overlay" aria-label="Close navigation" onClick={() => { setMenuOpen(false); menuButton.current && menuButton.current.focus(); }} />}
    <div ref={nav} id="mhcw-navigation" className={'mhcw-nav ' + (menuOpen ? 'mhcw-nav-open' : '')}>
      <div className="mhcw-brand"><span>MHC</span><strong>Workspace</strong></div>
      <div className="mhcw-nav-intro"><span>YOUR WORKSPACE</span><button type="button" className="mhcw-close-nav" aria-label="Close navigation" onClick={() => { setMenuOpen(false); menuButton.current && menuButton.current.focus(); }}>×</button></div>
      <LegacyHeader {...props} />
      <div className="mhcw-nav-footer"><Link to="/profile">Workspace profile</Link><button type="button" onClick={() => setClassic(true)}>Use classic layout</button></div>
    </div>
  </>;
}

export default function withWorkspaceHeader(LegacyHeader) {
  return function MhcWorkspaceHeader(props) { return <WorkspaceHeader {...props} LegacyHeader={LegacyHeader} />; };
}
